// StudioCard — one source, material or other item on the Studio canvas (pen
// "Kort", "Studio – detail design" §2), pure. The head (type icon, the kind
// in caps — a material's template, "Source · PDF" ("Source · File" when the
// file kind is unknown), "Item" for any other kind — and the AI mark), the title (serif, two lines, then an ellipsis; the
// full title is the tooltip), the preview's place, and one meta line: how far
// a source has been read, what a material was made from.
//
// The card's body only; where it sits, selection and dragging belong to the
// canvas (components/Studio).

export { StudioCard };

import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { AiMarker } from "../AiMarker/AiMarker.js";
import { Icon, type IconName } from "../Icon/Icon.js";
import { SourceReadStatus } from "../SourceReadStatus/SourceReadStatus.js";
import { CardName, type CardNaming } from "../Studio/CardName.js";
import type {
    GenericCard,
    MaterialCard,
    SourceCard,
} from "../Studio/studioCards.js";
import "./StudioCard.css";

type StudioCardProps = {
    readonly card: SourceCard | MaterialCard | GenericCard;
    // Its name, or its rename field (MF-1).
    readonly naming: CardNaming;
};

// A source of a file kind the app does not name (no format tag) takes the
// generic file icon; its eyebrow says "Source · File".
const iconOf = (card: StudioCardProps["card"]): IconName => {
    switch (card.type) {
        case "source":
            return card.format === "" ? "file" : "source";
        case "material":
            return "material";
        case "generic":
            return "file";
    }
};

const StudioCard = ({ card, naming }: StudioCardProps): ReactNode => {
    const { t } = useLingui();
    const eyebrow = ((): readonly string[] => {
        switch (card.type) {
            case "source":
                return [t`Source`, card.format === "" ? t`File` : card.format];
            // The kind the author knows it by: its template, else Material.
            case "material":
                return [
                    card.template === "quiz"
                        ? t`Quiz`
                        : card.template === "question"
                          ? t`Question`
                          : t`Material`,
                ];
            // Any other or unknown kind (the default): a generic item.
            case "generic":
                return [t`Item`, card.format];
        }
    })()
        .filter((s) => s !== "")
        .join(" · ");
    const status = ((): ReactNode => {
        switch (card.type) {
            case "source":
                return card.readStatus?.type === "source_read_ready" ? (
                    t`Read`
                ) : (
                    <SourceReadStatus status={card.readStatus} />
                );
            case "material": {
                if (card.planned) return t`Queued`;
                const [first, ...others] = card.basedOn;
                const from =
                    first === undefined
                        ? ""
                        : others.length === 0
                          ? t`From ${first}`
                          : t`From ${first} +${others.length}`;
                const versions =
                    card.versions === undefined || card.versions < 2
                        ? ""
                        : t`${plural(card.versions, {
                              one: "# version",
                              other: "# versions",
                          })}`;
                return [from, versions].filter((s) => s !== "").join(" · ");
            }
            case "generic":
                return null;
        }
    })();
    return (
        <div
            className="bilda-studio-card"
            data-kind={card.type}
            data-planned={
                card.type === "material" && card.planned ? "true" : undefined
            }
        >
            <div className="bilda-studio-card__eyebrow">
                <Icon name={iconOf(card)} size="xs" />
                <span className="bilda-studio-card__kind">{eyebrow}</span>
                {card.type === "material" && card.aiAuthored ? (
                    <AiMarker variant="chip" />
                ) : null}
            </div>
            <CardName
                title={card.title}
                kind="other"
                className="bilda-studio-card__title"
                naming={naming}
            />
            {/* The preview's place (a page's lines, drawn; no thumbnail yet). */}
            <div className="bilda-studio-card__preview" aria-hidden="true">
                <span />
                <span />
                <span />
            </div>
            {status === null || status === "" ? null : (
                <div className="bilda-studio-card__status">{status}</div>
            )}
        </div>
    );
};
